import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {AudioItem} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';

/**
 * Max time difference in seconds that the audio and video can be out of sync
 */
const MAXIMUM_SYNC_THRESHOLD = 0.5;

export class SyncAudioToPageClock extends CommonMethods {
  public audioItem: AudioItem;
  private syncing = false;
  private readonly boundSyncToPage;

  constructor(audioItem: AudioItem) {
    super();
    this.audioItem = audioItem;
    this.boundSyncToPage = this.syncToPage.bind(this);
  }

  public initSyncToPageClock(): void {
    this.audioItem.audioClips.page.on('time:updated', this.boundSyncToPage);
  }

  public unload(): void {
    this.audioItem.audioClips.page.off('time:updated', this.boundSyncToPage);
  }

  public async syncToPage(pageTime: number): Promise<void> {
    if (this.syncing) {
      return;
    }

    this.syncing = true;
    try {
      await this.syncItemToTime(pageTime);
      this.syncAudioPlaybackState();
    } finally {
      this.syncing = false;
    }
  }

  private async syncItemToTime(pageTime: number): Promise<void> {
    if (pageTime < this.audioItem.onPageStartTime || pageTime > this.audioItem.getOnPageEndTime()) {
      return;
    }

    const audioCurrentPlaybackTimeRespectiveToPage = this.audioItem.audioPlayer.getPlaybackCurrentTime() + this.audioItem.onPageStartTime;

    if (Math.abs(pageTime - audioCurrentPlaybackTimeRespectiveToPage) > MAXIMUM_SYNC_THRESHOLD) {
      await this.audioItem.seekToPageTime(pageTime);
    }
  }

  private syncAudioPlaybackState(): void {
    if (this.audioItem.shouldBePlaying() && !this.audioItem.audioPlayer.isPlaying()) {
      this.audioItem.play();
    } else if (!this.audioItem.shouldBePlaying() && this.audioItem.audioPlayer.isPlaying()) {
      this.audioItem.audioPlayer.pause();
    }
  }
}
